import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, CreditCard, Landmark, QrCode, ExternalLink } from "lucide-react";
import { T } from "@/lib/testIds";

const TYPE_META = {
  razorpay: { icon: CreditCard, name: "Razorpay link", desc: "Auto-tracks paid status via webhook" },
  upi: { icon: QrCode, name: "UPI ID / QR", desc: "Manual confirm after client pays" },
  external: { icon: ExternalLink, name: "External URL", desc: "Any web link (Stripe, PayPal, custom)" },
  directory: { icon: Landmark, name: "Insurer / AMC", desc: "Pre-loaded India providers" },
};

export default function PaymentOptions() {
  const { workspace } = useAuth();
  const [options, setOptions] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "razorpay", label: "", config: {} });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isDistributor = workspace?.business_type === "distributor";

  const load = async () => {
    setLoading(true);
    try {
      const [o, d] = await Promise.all([
        api.get("/payment-options"),
        api.get("/directory"),
      ]);
      setOptions(o.data);
      setDirectory(d.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setForm((f) => ({
      ...f,
      type: isDistributor ? "directory" : "razorpay",
    }));
    // eslint-disable-next-line
  }, [isDistributor]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCfg = (k, v) => setForm((f) => ({ ...f, config: { ...f.config, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload = { type: form.type, label: form.label || TYPE_META[form.type].name, config: form.config, active: true };
      if (form.type === "directory") {
        const entry = directory.find((d) => d.id === form.config.directory_id);
        if (!entry) { toast.error("Please choose a provider"); setSaving(false); return; }
        payload.label = form.label || entry.name;
      }
      await api.post("/payment-options", payload);
      toast.success("Payment option added");
      setOpen(false);
      setForm({ type: isDistributor ? "directory" : "razorpay", label: "", config: {} });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id, active) => {
    await api.patch(`/payment-options/${id}`, { active });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this payment option?")) return;
    await api.delete(`/payment-options/${id}`);
    load();
  };

  const availableTypes = isDistributor
    ? [["directory", TYPE_META.directory], ["external", TYPE_META.external]]
    : Object.entries(TYPE_META);

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">{isDistributor ? "Distributor · external providers" : "Collector · payment methods"}</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Payment options</h1>
        </div>
        <button
          data-testid={T.addOptionBtn}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press"
        >
          <Plus className="h-4 w-4" strokeWidth={2} aria-hidden="true" /> Add option
        </button>
      </header>

      <div className="p-6 lg:p-10">
        <div className="border co-hairline bg-white">
          {loading ? (
            <div className="p-8 text-sm text-[#52525B]">Loading…</div>
          ) : options.length === 0 ? (
            <div className="p-10 text-sm text-[#52525B]">
              No payment options yet. Add one so it appears in every reminder message.
            </div>
          ) : (
            options.map((o, i) => {
              const Meta = TYPE_META[o.type] || TYPE_META.external;
              const Icon = Meta.icon;
              return (
                <div key={o.id} className={`flex items-center gap-4 p-5 ${i < options.length - 1 ? "border-b co-hairline" : ""}`}>
                  <div className="w-10 h-10 bg-[#ECECFE] text-[#4338CA] grid place-items-center rounded-sm">
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{o.label}</div>
                    <div className="text-xs text-[#52525B] truncate">
                      {o.type === "upi" && `UPI: ${o.config?.upi_id || "—"}`}
                      {o.type === "external" && (o.config?.url || "—")}
                      {o.type === "directory" && (o.config?.provider_name || "")}
                      {o.type === "razorpay" && (
                        o.config?.has_key_id
                          ? `Razorpay merchant · ${o.config.key_id_last4 ? `key ending …${o.config.key_id_last4}` : "keys saved"}${o.config.has_webhook_secret ? " · webhook secret saved" : " · webhook secret NOT set"}`
                          : "Razorpay keys missing — add them to activate"
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={o.active} onCheckedChange={(v) => toggle(o.id, v)} data-testid={`payment-option-toggle-${o.id}`} />
                    <button onClick={() => remove(o.id)} className="p-2 hover:bg-[#FEF2F2] rounded-sm" data-testid={`payment-option-delete-${o.id}`}>
                      <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Add payment option</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="co-label">Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-2" data-testid={T.optionTypeSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableTypes.map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-[#52525B] mt-1">{TYPE_META[form.type].desc}</div>
            </div>

            <div>
              <Label className="co-label">Label (shown to clients)</Label>
              <Input data-testid={T.optionLabel} value={form.label} onChange={(e) => set("label", e.target.value)} className="mt-2" placeholder={TYPE_META[form.type].name} />
            </div>

            {form.type === "upi" && (
              <div>
                <Label className="co-label">UPI ID</Label>
                <Input data-testid={T.optionUpiId} value={form.config?.upi_id || ""} onChange={(e) => setCfg("upi_id", e.target.value)} required className="mt-2" placeholder="business@upi" />
              </div>
            )}

            {form.type === "external" && (
              <div>
                <Label className="co-label">Payment URL</Label>
                <Input data-testid={T.optionUrl} type="url" value={form.config?.url || ""} onChange={(e) => setCfg("url", e.target.value)} required className="mt-2" placeholder="https://…" />
              </div>
            )}

            {form.type === "directory" && (
              <div>
                <Label className="co-label">Provider</Label>
                <Select value={form.config?.directory_id || ""} onValueChange={(v) => setCfg("directory_id", v)}>
                  <SelectTrigger className="mt-2" data-testid={T.optionDirectory}><SelectValue placeholder="Choose insurer or AMC…" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <div className="co-label px-2 pt-2 pb-1">Insurers</div>
                    {directory.filter((d) => d.category === "insurer").map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                    <div className="co-label px-2 pt-3 pb-1">Asset Management Companies</div>
                    {directory.filter((d) => d.category === "amc").map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.type === "razorpay" && (
              <div className="space-y-3">
                <div className="text-xs text-[#52525B] bg-[#FAFAFA] border co-hairline p-3">
                  Each business uses their <b>own Razorpay account</b>. Paste your keys below — payments will land in
                  your bank via Razorpay and CollectOne will auto-mark clients as Paid.
                  <div className="mt-1">
                    Get them at{" "}
                    <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="text-[#4338CA] font-semibold underline">
                      Razorpay Dashboard → Settings → API Keys
                    </a>.
                  </div>
                </div>
                <div>
                  <Label className="co-label">Razorpay Key ID</Label>
                  <Input
                    data-testid={T.rzpKeyIdInput}
                    value={form.config?.key_id || ""}
                    onChange={(e) => setCfg("key_id", e.target.value.trim())}
                    required
                    className="mt-2 font-mono text-sm"
                    placeholder="rzp_live_… or rzp_test_…"
                  />
                </div>
                <div>
                  <Label className="co-label">Razorpay Key Secret</Label>
                  <Input
                    data-testid={T.rzpKeySecretInput}
                    type="password"
                    value={form.config?.key_secret || ""}
                    onChange={(e) => setCfg("key_secret", e.target.value.trim())}
                    required
                    className="mt-2 font-mono text-sm"
                    placeholder="Paste the secret shown once at key creation"
                  />
                </div>
                <div>
                  <Label className="co-label">Webhook Secret (optional but recommended)</Label>
                  <Input
                    data-testid={T.rzpWebhookSecretInput}
                    type="password"
                    value={form.config?.webhook_secret || ""}
                    onChange={(e) => setCfg("webhook_secret", e.target.value.trim())}
                    className="mt-2 font-mono text-sm"
                    placeholder="Any random string — you'll set the same in Razorpay dashboard"
                  />
                  {workspace?.id && (
                    <div className="mt-2 text-xs text-[#52525B] bg-white border co-hairline p-3 rounded-sm">
                      <div className="co-label mb-1">Webhook URL — paste this in Razorpay Dashboard → Settings → Webhooks</div>
                      <div className="font-mono text-[11px] break-all select-all">
                        {`${API_BASE}/webhooks/razorpay/${workspace.id}`}
                      </div>
                      <div className="mt-1">Enable events: <b>payment_link.paid</b> and <b>payment.captured</b></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm font-semibold border border-[#0A0A0B] rounded-sm co-btn-press hover:bg-[#F4F4F5]">
                Cancel
              </button>
              <button data-testid={T.optionSubmit} type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press disabled:opacity-60">
                {saving ? "Saving…" : "Add option"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
